import type { AstroChart, NatalParams, NatalResponse, NatalSummaryFull } from '@/api/schema'
import type { SessionStore } from '@/app/state/session'
import { api } from '@/app/utils/api'
import { createContext } from 'preact'
import { useContext } from 'preact/hooks'
import { create, type StoreApi, type UseBoundStore } from 'zustand'

type NatalState = {
  loading: boolean
  chart: AstroChart | null
  summaryFull: NatalSummaryFull | null
  cacheKey: string | null

  reset(): void
  fetch(): Promise<void>
}

export type NatalStore = UseBoundStore<StoreApi<NatalState>>

export const NatalStoreContext = createContext<NatalStore | null>(null)

export function useNatal<T>(selector: (state: NatalState) => T) {
  const store = useContext(NatalStoreContext)

  if (!store) {
    throw new Error('NatalProvider is required')
  }

  return store(selector)
}

export const createNatalStore = (sessionStore: SessionStore) => (
  create<NatalState>((set, get) => ({
    loading: true,
    chart: null,
    summary: null,
    summaryFull: null,
    cacheKey: null,

    reset() {
      set({
        loading: true,
        chart: null,
        summaryFull: null,
        cacheKey: null,
      })
    },

    async fetch() {
      const state = get()
      const session = sessionStore.getState()

      const birthDate = session.profile?.birthDate
      const birthTime = session.profile?.birthTime
      const locationId = session.profile?.location?.id
      const cacheKey = `${birthDate ?? ''}:${birthTime ?? ''}:${locationId ?? ''}`

      if (!birthDate) {
        throw new Error('Заполните профиль')
      }

      if (state.cacheKey === cacheKey && state.chart) {
        return
      }

      set({
        loading: true,
        chart: null,
        summaryFull: null,
      })

      const baseResp = await api.post<NatalResponse>(
        '/natal',
        {
          birthDate: birthDate,
          birthTime: birthTime ?? undefined,
          locationId: locationId,
        } satisfies NatalParams,
      )

      set({
        chart: baseResp.data.chart,
        cacheKey,
        loading: false,
      })
    },
  }))
)
